import {
  Employee as EmployeeDto,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';

import { EmployeeSource } from '@/types/employees';

/**
 * Fixtures for the dashboard "Unmanaged devices and employees" alert tile and
 * the MDM enrollment modal behind its Resolve button.
 *
 * Pick a scenario in `npm run dev:mock` by adding `?mdmScenario=<name>` to the
 * URL (it survives tab switches on the dashboard):
 *
 *   default  - a mix of every row shape (see the comments on each fixture)
 *   many     - long lists, to check scrolling in the tile and the modal
 *   allClear - nothing unmanaged and nobody without a device
 *   loading  - the metrics requests hang, so the tile stays in its skeleton
 *   error    - the metrics requests fail with a 500, so the tile shows its error
 *   sendError - the default rows, but every enrollment email send fails, to try
 *               the error path of the row menu and of the Resolve modal
 */
export type MdmMockScenario =
  | 'default'
  | 'many'
  | 'allClear'
  | 'loading'
  | 'error'
  | 'sendError';

const SCENARIOS: MdmMockScenario[] = [
  'default',
  'many',
  'allClear',
  'loading',
  'error',
  'sendError',
];

export const getMdmMockScenario = (): MdmMockScenario => {
  // Also used by Vitest and Storybook, where there is no real URL to read
  if (typeof window === 'undefined') {
    return 'default';
  }
  const value = new URLSearchParams(window.location.search).get('mdmScenario');
  return SCENARIOS.find(scenario => scenario === value) ?? 'default';
};

// Ids that make the mock backend answer with a 500, so the failure toasts and
// "row stays put" behaviour can be tried by hand. The send failure only applies
// when this person is the only recipient (their row menu); a bulk send that
// includes them succeeds, so the whole section can still be resolved from the
// modal. Use ?mdmScenario=sendError to make every send fail instead.
export const SEND_FAILS_EMPLOYEE_ID = 'mdm-mock-employee-send-fails';
export const EXEMPT_FAILS_ASSET_ID = 'mdm-mock-asset-exempt-fails';
export const EXEMPT_FAILS_EMPLOYEE_ID = 'mdm-mock-employee-exempt-fails';

// Devices the org already manages and employees who already have one; they
// only feed the other numbers on the metrics endpoints.
const MANAGED_DEVICES = 41;
const EMPLOYEES_WITH_DEVICES = 58;

type MockAsset = {
  id: string;
  name?: string;
  serial_number?: string;
  assigned_to: string;
  assigned_to_employee: { id: string; first_name: string; last_name: string };
  device: { id: string; os_type: string; hardware_model: string };
  mdm_status_override?: string | null;
};

type MdmMockDataset = {
  employees: EmployeeDto[];
  assets: MockAsset[];
  /** Employees that have no device at all (no asset in `assets`) */
  noDeviceEmployeeIds: string[];
};

const makeEmployee = (
  id: string,
  firstName: string,
  lastName: string,
  extras: Partial<EmployeeDto> = {}
): EmployeeDto => ({
  id,
  first_name: firstName,
  last_name: lastName,
  email: `${firstName}.${lastName}@thesiscare.com`
    .toLowerCase()
    .replace(/[^a-z.@]/g, ''),
  job_title: 'Employee',
  created_at: '2024-01-15',
  updated_at: '2024-01-15',
  status: EmployeeStatusEnum.Active,
  source: EmployeeSource.Manual,
  ...extras,
});

const makeAsset = (
  id: string,
  owner: EmployeeDto,
  hardwareModel: string,
  extras: Partial<MockAsset> = {}
): MockAsset => ({
  id,
  name: hardwareModel,
  serial_number: `SN-${id.toUpperCase().slice(-8)}`,
  assigned_to: owner.id,
  assigned_to_employee: {
    id: owner.id,
    first_name: owner.first_name ?? '',
    last_name: owner.last_name ?? '',
  },
  device: {
    id: `device-${id}`,
    os_type: hardwareModel.includes('Mac') ? 'darwin' : 'windows',
    hardware_model: hardwareModel,
  },
  ...extras,
});

const buildDefaultDataset = (): MdmMockDataset => {
  // Unmanaged devices ---------------------------------------------------------
  const jordan = makeEmployee('mdm-mock-jordan', 'Jordan', 'Reyes', {
    job_title: 'Designer',
  });
  const sam = makeEmployee('mdm-mock-sam', 'Sam', 'Patel');
  // Owns two unmanaged devices: two rows in the tile, one grouped row in the modal
  const priya = makeEmployee('mdm-mock-priya', 'Priya', 'Nair');
  const marcus = makeEmployee('mdm-mock-marcus', 'Marcus', 'Webb');
  // No email address, so an enrollment email can't be sent to them
  const tomas = makeEmployee('mdm-mock-tomas', 'Tomás', 'Alvarez', {
    email: undefined,
  });
  const erin = makeEmployee(SEND_FAILS_EMPLOYEE_ID, 'Erin', 'Sendfails');
  const dana = makeEmployee('mdm-mock-dana', 'Dana', 'Exemptfails');

  // Employees without a device -----------------------------------------------
  const aisha = makeEmployee('mdm-mock-aisha', 'Aisha', 'Khan', {
    job_title: 'Operations',
  });
  // No job title, so the row subtitle falls back to "No device assigned"
  const liam = makeEmployee('mdm-mock-liam', 'Liam', "O'Connor", {
    job_title: undefined,
  });
  const noor = makeEmployee('mdm-mock-noor', 'Noor', 'Hassan', {
    job_title: 'Finance',
    email: undefined,
  });
  const cole = makeEmployee(EXEMPT_FAILS_EMPLOYEE_ID, 'Cole', 'Exemptfails', {
    job_title: 'Support',
  });

  return {
    employees: [
      jordan,
      sam,
      priya,
      marcus,
      tomas,
      erin,
      dana,
      aisha,
      liam,
      noor,
      cole,
    ],
    assets: [
      makeAsset('mdm-mock-asset-jordan', jordan, 'MacBook Pro 14"', {
        serial_number: 'FVFXK21ABC',
      }),
      makeAsset('mdm-mock-asset-sam', sam, 'Dell Latitude 5450', {
        serial_number: 'DL5450X92',
      }),
      makeAsset('mdm-mock-asset-priya-1', priya, 'MacBook Air M3'),
      makeAsset('mdm-mock-asset-priya-2', priya, 'Lenovo ThinkPad X1'),
      // No name or serial, so the row falls back to the hardware model alone
      makeAsset('mdm-mock-asset-marcus', marcus, 'Surface Laptop 5', {
        name: undefined,
        serial_number: undefined,
      }),
      makeAsset('mdm-mock-asset-tomas', tomas, 'HP EliteBook 840'),
      makeAsset('mdm-mock-asset-erin', erin, 'MacBook Pro 16"'),
      makeAsset(EXEMPT_FAILS_ASSET_ID, dana, 'Dell XPS 13'),
    ],
    noDeviceEmployeeIds: [aisha, liam, noor, cole].map(employee => employee.id),
  };
};

const MANY_COUNT = 30;

const buildManyDataset = (): MdmMockDataset => {
  const owners = Array.from({ length: MANY_COUNT }, (_, index) =>
    makeEmployee(`mdm-mock-many-owner-${index + 1}`, 'Owner', `${index + 1}`)
  );
  const noDevice = Array.from({ length: MANY_COUNT }, (_, index) =>
    makeEmployee(
      `mdm-mock-many-nodevice-${index + 1}`,
      'Newhire',
      `${index + 1}`,
      { job_title: index % 2 === 0 ? 'Sales' : undefined }
    )
  );
  return {
    employees: [...owners, ...noDevice],
    assets: owners.map((owner, index) =>
      makeAsset(
        `mdm-mock-many-asset-${index + 1}`,
        owner,
        index % 2 === 0 ? 'MacBook Pro 14"' : 'Dell Latitude 5450'
      )
    ),
    noDeviceEmployeeIds: noDevice.map(employee => employee.id),
  };
};

const EMPTY_DATASET: MdmMockDataset = {
  employees: [],
  assets: [],
  noDeviceEmployeeIds: [],
};

const defaultDataset = buildDefaultDataset();

const datasets: Record<MdmMockScenario, MdmMockDataset> = {
  default: defaultDataset,
  sendError: defaultDataset,
  many: buildManyDataset(),
  allClear: EMPTY_DATASET,
  // These two never get far enough to need rows
  loading: EMPTY_DATASET,
  error: EMPTY_DATASET,
};

// Every fixture employee/asset, whatever the scenario, so lookups by id keep
// working if the URL param changes mid-session.
export const allMdmMockEmployees = [
  ...datasets.default.employees,
  ...datasets.many.employees,
];
export const allMdmMockAssets = [
  ...datasets.default.assets,
  ...datasets.many.assets,
];

// "Mark not applicable" changes live here so refetches agree with what was
// just clicked, until the page reloads (same approach as the scheduled tasks mock).
const exemptedAssetIds = new Set<string>();
const exemptedEmployeeIds = new Set<string>();

export const exemptMockAsset = (assetId: string) =>
  exemptedAssetIds.add(assetId);
export const unexemptMockAsset = (assetId: string) =>
  exemptedAssetIds.delete(assetId);
export const exemptMockEmployee = (employeeId: string) =>
  exemptedEmployeeIds.add(employeeId);

export const resetMdmEnrollmentMock = () => {
  exemptedAssetIds.clear();
  exemptedEmployeeIds.clear();
};

export const getUnmanagedMockAssets = (scenario: MdmMockScenario) =>
  datasets[scenario].assets.filter(asset => !exemptedAssetIds.has(asset.id));

export const getNoDeviceMockEmployees = (scenario: MdmMockScenario) =>
  datasets[scenario].employees.filter(
    employee =>
      datasets[scenario].noDeviceEmployeeIds.includes(employee.id) &&
      !exemptedEmployeeIds.has(employee.id)
  );

const zeroBreakdown = {
  total: 0,
  versions: 0,
  at_eol: 0,
  near_eol: 0,
  supported: 0,
  unknown: 0,
  version_details: [],
};

const enrollment = (enrolled: number, unenrolled: number) => ({
  total: enrolled + unenrolled,
  mdm_enrolled: enrolled,
  mdm_unenrolled: unenrolled,
});

export const buildMdmMetricsResponse = (scenario: MdmMockScenario) => {
  const unmanaged = getUnmanagedMockAssets(scenario);
  return {
    devices: {
      managed_devices: MANAGED_DEVICES,
      unmanaged_devices: unmanaged.length,
      unmanaged_devices_ids: unmanaged.map(asset => asset.id),
      assigned_enrolled: { darwin: zeroBreakdown, windows: zeroBreakdown },
      unassigned_enrolled: { darwin: zeroBreakdown, windows: zeroBreakdown },
    },
    employees_without_assets: getNoDeviceMockEmployees(scenario).length,
  };
};

export const buildDeviceMetricsResponse = (scenario: MdmMockScenario) => {
  const unmanagedCount = getUnmanagedMockAssets(scenario).length;
  const withoutDevices = getNoDeviceMockEmployees(scenario).length;
  return {
    employees: {
      total: EMPLOYEES_WITH_DEVICES + withoutDevices,
      active: {
        total: EMPLOYEES_WITH_DEVICES + withoutDevices,
        with_devices: EMPLOYEES_WITH_DEVICES,
        without_devices: withoutDevices,
      },
      inactive: { total: 3 },
      unknown: { total: 0 },
    },
    devices: {
      total: MANAGED_DEVICES + unmanagedCount,
      active: {
        total: MANAGED_DEVICES + unmanagedCount,
        assigned_to_active_employees: enrollment(
          MANAGED_DEVICES,
          unmanagedCount
        ),
        assigned_to_inactive_employees: enrollment(0, 0),
        unassigned: enrollment(0, 0),
      },
      inactive: 0,
    },
  };
};
